import { useState, ChangeEvent } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Trash2, ArrowLeftRight } from "lucide-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function UrlEncoderTool() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState<"encode" | "decode">("encode");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const processText = (text: string, currentMode: "encode" | "decode") => {
    if (!text) {
      setOutput("");
      setError(null);
      return;
    }
    try {
      if (currentMode === "encode") {
        setOutput(encodeURIComponent(text));
      } else {
        setOutput(decodeURIComponent(text));
      }
      setError(null);
    } catch (e) {
      setError("Invalid URI component");
      setOutput("");
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    processText(val, mode);
  };

  const toggleMode = () => {
    const newMode = mode === "encode" ? "decode" : "encode";
    setMode(newMode);
    setInput(output); // swap
    processText(output, newMode);
  };

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="URL Encoder & Decoder Online"
      description="Encode and decode URL components instantly with our free online tool. Perfect for web developers and API testing."
      h1="URL Encoder & Decoder"
      category="Encoding Tools"
      categoryPath="/encoding-tools"
      howToUse={[
        "Select 'Encode' or 'Decode' mode.",
        "Paste your URL component in the input area.",
        "The result will be updated in real-time."
      ]}
      faq={[
        { question: "What does URL encoding do?", answer: "It replaces special characters in a URL with a percent (%) sign followed by their corresponding hex value." }
      ]}
    >
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button onClick={toggleMode} variant="outline" className="gap-2 w-full md:w-auto">
            <ArrowLeftRight className="h-4 w-4" />
            Switch to {mode === "encode" ? "Decode" : "Encode"}
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              {mode === "encode" ? "Text to Encode" : "URL Encoded Text"}
            </label>
            <Textarea
              placeholder={mode === "encode" ? "https://example.com/search?q=hello world" : "https%3A%2F%2Fexample.com%2Fsearch%3Fq%3Dhello%20world"}
              className="min-h-[300px]"
              value={input}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              {mode === "encode" ? "Encoded Output" : "Decoded Output"}
            </label>
            <Textarea
              className="min-h-[300px] bg-slate-50"
              value={error ? error : output}
              readOnly
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleCopy} disabled={!!error || !output} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Output"}
          </Button>
          <Button onClick={() => { setInput(""); setOutput(""); setError(null); }} variant="ghost" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}
